import Cookies from "js-cookie";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import {
  formatCurrencyVND,
  formatNumberWithCommas,
  resolveLocaleFromLang,
} from "./numberFormat";
import { normalizeLang } from "./normalizeLang";

const trimTrailingSlash = (value = "") => value.replace(/\/+$/, "");

const getWindowOrigin = () =>
  typeof window !== "undefined" ? window.location.origin : "";

const getWindowHostname = () =>
  typeof window !== "undefined" ? window.location.hostname : "";

const resolveApiHost = () => {
  const envHost = (process.env.REACT_APP_API || "").trim();
  if (envHost) {
    try {
      const parsed = new URL(envHost)
      const envHostname = parsed.hostname
      const browserHostname = getWindowHostname()
      const envIsLoopback =
        envHostname === "localhost" ||
        envHostname === "127.0.0.1" ||
        envHostname === "::1"
      const browserIsLoopback =
        !browserHostname ||
        browserHostname === "localhost" ||
        browserHostname === "127.0.0.1" ||
        browserHostname === "::1"

      // When the dev app is opened through a remote hostname/IP/tunnel, a
      // localhost API env would point at the user's browser machine. Use the
      // CRA proxy on the current origin instead.
      if (envIsLoopback && !browserIsLoopback) {
        return "";
      }
    } catch {
      // Fall back to the configured value below.
    }
    return trimTrailingSlash(envHost);
  }
  return getWindowOrigin();
};

const resolveImageHost = () => {
  const envImageHost = (process.env.REACT_APP_IMAGE_HOST || "").trim();
  if (envImageHost) {
    return trimTrailingSlash(envImageHost);
  }
  const apiHost = resolveApiHost();
  if (apiHost) {
    return apiHost;
  }
  return getWindowOrigin();
};

const resolveCookieDomain = () => {
  const envUrl = (process.env.REACT_APP_URL || "").trim()
  if (envUrl) {
    try {
      // Support both full URLs (e.g. http://localhost:3000) and hostnames (e.g. localhost:3000).
      const parsed = envUrl.includes("://") ? new URL(envUrl) : new URL(`http://${envUrl}`)
      const hostname = parsed.hostname
      // For localhost/IPs, omit `domain` so the cookie becomes host-only (most reliable for local dev).
      if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return undefined
      return hostname
    } catch {
      // Best-effort cleanup if envUrl isn't URL-parseable.
      const cleaned = envUrl.replace(/^https?:\/\//i, "").split("/")[0]
      const hostname = cleaned.split(":")[0]
      if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return undefined
      return hostname
    }
  }
  const hostname = getWindowHostname();
  if (hostname) {
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return undefined
    return hostname;
  }
  const apiHost = resolveApiHost();
  if (apiHost) {
    try {
      const parsed = new URL(apiHost)
      const hostname = parsed.hostname
      if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return undefined
      return hostname;
    } catch {
      return apiHost;
    }
  }
  return undefined;
};

const buildApiBaseUrl = () => {
  const host = resolveApiHost();
  return host ? `${host}/api/v1/` : "/api/v1/";
};

const cookieOptions = () => {
  const domain = resolveCookieDomain();
  const options = { path: "/" };
  if (domain) {
    options.domain = domain;
  }
  return options;
};

const getWindowSearch = () =>
  typeof window !== "undefined" ? window.location.search || "" : ""

export const isMockModeEnabled = (search = getWindowSearch()) => {
  try {
    const params = new URLSearchParams(typeof search === "string" ? search : "")
    const raw =
      params.get("mock") ??
      params.get("useMock") ??
      params.get("use_mock") ??
      params.get("demo") ??
      params.get("mock_data")

    // Query param overrides env default.
    // Enable: ?mock=1 / true / on
    // Disable: ?mock=0 / false / off
    if (raw !== null) {
      const normalized = String(raw).trim().toLowerCase()
      if (["0", "false", "no", "off"].includes(normalized)) return false
      if (["1", "true", "yes", "on"].includes(normalized)) return true
    }
  } catch {
    // ignore
  }

  // Default: env flag.
  return process.env.REACT_APP_USE_MOCK_DATA === "true"
}

export const isAuthenticated = () => {
  const token = Cookies.get(process.env.REACT_APP_COOKIE_KEY)
  if (typeof token === "string") {
    const cleaned = token
      .trim()
      .replace(/^"+|"+$/g, "")
      .replace(/^Bearer\s+/i, "")
      .trim()
    if (cleaned) return cleaned
  }

  return undefined
};

export const API = axios.create({
  baseURL: buildApiBaseUrl(),
});

export const headerWithAuthorization = (headers = {}) => {
  const token = isAuthenticated();
  const lang = normalizeLang(localStorage.getItem("lang"));
  const authorization = token ? `Bearer ${token}` : "";
  return {
    ...headers,
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: authorization,
    "X-localization": lang || "en",
  };
};

export const initHeader = () => {
  let token = isAuthenticated();
  let lang = normalizeLang(localStorage.getItem("lang"));
  API.defaults.headers.common["Authorization"] = token ? `Bearer ${token}` : "";
  API.defaults.headers.common["X-localization"] = lang || "en";
};

initHeader();

// Flag to prevent multiple redirects
let isRedirectingToLogin = false;

API.interceptors.request.use(
  async (config) => {
    const token = isAuthenticated()
    const lang = normalizeLang(localStorage.getItem("lang"))

    config.headers = config.headers || {}

    // Keep auth/localization headers in sync with the latest client state.
    // This fixes mixed-language responses when users switch UI language (MIN-1248 Bug #29).
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      // Don't send an empty Authorization header: some backends treat it as an invalid token.
      delete config.headers.Authorization
    }
    config.headers["X-localization"] = lang || "en"

    return config
  },
  async (error) => Promise.reject(error)
)

const emitAuthLogout = (detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent("tna:auth:logout", { detail }));
  } catch (e) {
    // Fallback for environments where CustomEvent is not supported.
    window.dispatchEvent(new Event("tna:auth:logout"));
  }
};

API.interceptors.response.use(
  async (res) => {
    const contentType = res?.headers?.["content-type"] || ""
    const isJson = contentType.includes("application/json")
    const data = res?.data

    if (isJson && data && typeof data === "object" && !Array.isArray(data)) {
      if (data.success === false) {
        return Promise.reject({ response: res })
      }
      if (typeof data.success === "undefined") {
        data.success = true
      }
    }

    return res
  },
  async (error) => {
    const status = error?.response?.status;
    const errorData = error?.response?.data;
    const apiErrorLog = {
      code: errorData?.error?.code,
      message: errorData?.error?.message || errorData?.message,
      request_id: errorData?.error?.request_id,
      status,
      method: error?.config?.method,
      baseURL: error?.config?.baseURL,
      url: error?.config?.url,
    }

    if (status >= 400) {
      console.error("[API Error]", apiErrorLog);
    }

    // Log critical/system errors for monitoring
    if (errorData?.error?.severity === "critical" || status >= 500) {
      console.error("[Critical API Error]", apiErrorLog);
    }

    if (status === 401) {
      // Clear all auth data
      const options = cookieOptions();
      Cookies.remove(process.env.REACT_APP_COOKIE_KEY, options);
      Cookies.remove(process.env.REACT_APP_COOKIE_KEY);
      Cookies.remove("roles", options);
      Cookies.remove("roles");

      // Clear localStorage user data
      window.localStorage.removeItem("userInfo");
      emitAuthLogout({ reason: "unauthorized" });

      // In mock/demo mode, allow the UI to render without a backend session.
      // Many pages call auth endpoints on mount; redirecting to /login would make mock flows unusable.
      if (isMockModeEnabled()) {
        return Promise.reject(error?.response?.data ?? error)
      }

      // Redirect to login page (prevent multiple redirects)
      if (!isRedirectingToLogin && window.location.pathname !== "/login") {
        isRedirectingToLogin = true;

        // Store the current path for redirect after login (optional)
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== "/" && currentPath !== "/login") {
          window.sessionStorage.setItem("redirectAfterLogin", currentPath);
        }

        // Redirect to login
        window.location.href = "/login";
      }
    }

    return Promise.reject(error?.response?.data ?? error);
  }
);

/**
 * Sets a cookie with the given value and expiration.
 * @param {string} value - The value to store in the cookie
 * @param {string|number} expires - Expiration time. Can be:
 *   - A datetime string from backend (e.g., "2026-01-22T10:30:00.000000Z")
 *   - A number of seconds (legacy support)
 *   - "session" to create a session cookie (cleared on browser close)
 *   - undefined/null (uses REACT_APP_EXPIRES_COOKIE env var as fallback)
 * @param {string} key - The cookie key (defaults to REACT_APP_COOKIE_KEY)
 */
export const setCookie = (
  value = "",
  expires,
  key = process.env.REACT_APP_COOKIE_KEY
) => {
  const options = cookieOptions();
  let expiresDate;

  if (expires === "session") {
    Cookies.set(key, value, options);
    return;
  }

  if (!expires) {
    // Fallback: use env variable (assumed to be in seconds)
    const fallbackSeconds = parseInt(process.env.REACT_APP_EXPIRES_COOKIE, 10) || 86400;
    expiresDate = new Date(Date.now() + fallbackSeconds * 1000);
  } else if (typeof expires === "string" && expires.includes("T")) {
    // Backend returns ISO datetime string (e.g., "2026-01-22T10:30:00.000000Z")
    expiresDate = new Date(expires);
  } else {
    // Legacy: expires is a number of seconds
    expiresDate = new Date(Date.now() + Number(expires) * 1000);
  }

  Cookies.set(key, value, { ...options, expires: expiresDate });
};

export const logOut = (redirect = false) => {
  const opts = cookieOptions();

  // Clear all cookies
  Cookies.remove(process.env.REACT_APP_COOKIE_KEY, { ...opts, path: "/" });
  Cookies.remove("roles", { ...opts, path: "/" });

  // Clear localStorage
  window.localStorage.removeItem("userInfo");
  window.localStorage.removeItem("permissions");
  emitAuthLogout({ reason: "manual" });

  // Reset redirect flag
  isRedirectingToLogin = false;

  // Redirect to login
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

/**
 * Remove user session (alias for logOut without redirect).
 * Used by error handling module.
 */
export const removeUserSession = () => {
  logOut(false);
};

export { resolveApiHost as getApiHost, resolveImageHost as getImageHost };

/**
 * Resolve image-like values (disk path, /storage path, or absolute URL) into a URL
 * that is reachable from the browser environment.
 *
 * This helps avoid cases where the backend returns absolute URLs using an internal
 * Docker hostname or a missing port, which makes <img> previews fail on the FE.
 */
export const resolveImageUrl = (value) => {
  if (value == null) return value;

  const raw = String(value).trim();
  if (!raw) return raw;

  // Data URLs / blob URLs should be used as-is.
  if (raw.startsWith("data:") || raw.startsWith("blob:")) return raw;

  const imageHost = resolveImageHost();
  if (!imageHost) return raw;

  // If backend returns absolute URL for public storage, normalize origin to FE-configured host.
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const url = new URL(raw);
      if (url.pathname.startsWith("/storage/")) {
        return `${imageHost}${url.pathname}${url.search}${url.hash}`;
      }
      return raw;
    } catch (e) {
      return raw;
    }
  }

  // Absolute path from backend (eg. "/storage/avatars/..")
  if (raw.startsWith("/")) {
    return `${imageHost}${raw}`;
  }

  // Relative "storage/.." path
  if (raw.startsWith("storage/")) {
    return `${imageHost}/${raw}`;
  }

  // Public disk path (eg. "avatars/..") -> "/storage/avatars/.."
  return `${imageHost}/storage/${raw}`;
};

export const getUserInfo = () => {
  let token = isAuthenticated();
  let userInfo = {};
  if (token) {
    userInfo = jwtDecode(token);
  }
  return userInfo;
};

const fetchLoggedInUser = async () => {
  try {
    const response = await API.get("auth/profile");
    const user = response.data.data;
    window.localStorage.setItem("userInfo", JSON.stringify(user));
    return user;
  } catch (error) {
    try {
      const response = await API.get("auth/user");
      const user = response.data.data;
      window.localStorage.setItem("userInfo", JSON.stringify(user));
      return user;
    } catch (fallbackError) {
      console.error("Failed to load user profile:", fallbackError);
      return {};
    }
  }
};

export const getLoggedInUser = async ({ force = false } = {}) => {
  if (!force) {
    const raw = window.localStorage.getItem("userInfo");
    if (raw) {
      try {
        const cached = JSON.parse(raw);
        if (cached) return cached;
      } catch (e) {
        // ignore parse errors and re-fetch
      }
    }
  }

  return fetchLoggedInUser();
};

/**
 * Force refresh logged-in user (bypass localStorage cache).
 * Useful after RBAC changes so UI can reflect updated permissions/roles.
 */
export const refreshLoggedInUser = async () => {
  const user = await fetchLoggedInUser();

  // Notify app layers that cached userInfo has been refreshed.
  // The payload is optional; listeners can re-read from localStorage if needed.
  try {
    window.dispatchEvent(
      new CustomEvent("tna:userInfo:updated", { detail: { user } })
    );
  } catch (e) {
    window.dispatchEvent(new Event("tna:userInfo:updated"));
  }

  return user;
};

export const formatTime = (data, format) => {
  let stringFormat = format || "YYYY/MM/DD HH:mm:ss";
  const m = moment(data);

  return m.isValid() ? m.format(stringFormat) : "";
};

export const getDateTimeDisplayFormat = (locale) => {
  const normalized = (locale || "").toLowerCase();
  if (normalized.startsWith("vi")) {
    return "DD/MM/YYYY HH:mm";
  }
  if (normalized.startsWith("ja")) {
    return "YYYY/MM/DD HH:mm";
  }
  return "MM/DD/YYYY HH:mm";
};

export const getDateDisplayFormat = (locale) => {
  const normalized = (locale || "").toLowerCase();
  if (normalized.startsWith("vi")) {
    return "DD/MM/YYYY";
  }
  if (normalized.startsWith("ja")) {
    return "YYYY/MM/DD";
  }
  return "MM/DD/YYYY";
};

export const formatDateTimeByLocale = (data, locale) => {
  return formatTime(data, getDateTimeDisplayFormat(locale));
};

export const formatDateByLocale = (data, locale) => {
  if (!data) return "";

  const value = String(data);
  const isIsoWithTimezone =
    value.includes("T") &&
    (value.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(value));

  const m = isIsoWithTimezone ? moment.utc(value) : moment(value);
  return m.isValid() ? m.format(getDateDisplayFormat(locale)) : "";
};

export const formatTimePayload = (data, format) => {
  if (!data) return null;
  let stringFormat = format || "YYYY-MM-DD HH:mm:ss";

  // Nếu input là ISO string có timezone (VD: "2026-03-20T00:00:00+07:00"),
  // tách lấy phần ngày để tránh moment tự ý convert timezone
  if (typeof data === "string" && data.includes("T")) {
    const tzMatch = data.match(/[+-]\d{2}:\d{2}$/);
    if (tzMatch) {
      data = data.replace(tzMatch[0], "+00:00");
    }
  }

  const m = moment(data);
  return m.isValid() ? m.format(stringFormat) : null;
};

export const displayVND = (money) => {
  return formatCurrencyVND(money);
};

export const getCurrentPage = (url) => {
  let searchObj = new URLSearchParams(url);
  return searchObj.has("page") ? parseInt(searchObj.get("page")) : 1;
};

export const getDataSearch = (url) => {
  let searchArr = new URLSearchParams(url),
    dataSearch = {};
  if (searchArr.keys().length !== 0) {
    for (var item of searchArr.entries()) {
      dataSearch[item[0]] = item[1];
    }
  }
  return dataSearch;
};

export const setParams = (localtion, callback) => {
  let searchParams = new URLSearchParams(localtion);
  for (var key of searchParams.entries()) {
    callback(key[0], key[1]);
  }
};

export const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== "" && v !== undefined && v !== null)
          query.append(`${key}[]`, v);
      });
    } else if (value !== "" && value !== undefined && value !== null) {
      query.append(key, value);
    }
  });
  return query.toString();
};

export function numberFormat(value, decimals = 0) {
  const resolvedLocale = resolveLocaleFromLang(
    typeof localStorage === "undefined" ? null : localStorage.getItem("lang")
  );
  const formatted = formatNumberWithCommas(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    locale: resolvedLocale,
  });
  return formatted || "0";
}

/**
 * Formats a number as currency with Vietnamese formatting
 * @param value - The numeric value to format
 * @param currency - Currency type (default: 'vnd')
 * @param language - Language for unit display (default: 'vi')
 * @returns Formatted currency string
 */
export function formatMoney(value, language = "vi") {
  if (value === null || value === undefined || isNaN(value)) {
    return "0";
  }
  const ONE_MILLION = 1000000;

  // For values >= 1,000,000 (1 million)
  if (value >= ONE_MILLION) {
    const ret = value / ONE_MILLION;

    // Determine unit based on language
    const unit = language === "en" ? " Mil" : " Tr";

    // Check if it's a whole number
    if (ret === Math.floor(ret)) {
      return numberFormat(ret) + unit;
    }

    // Format with one decimal place
    return numberFormat(ret, 1) + unit;
  }

  // For values < 1,000,000, just format normally
  return numberFormat(value);
}

export const formatSalaryWithUnit = (salary, unit = "vnd") => {
  const formattedSalary = numberFormat(salary);
  return unit === "usd" ? `${formattedSalary} USD` : `${formattedSalary} VND`;
};

export const downloadFile = (fileUrl, fileName) => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};


// Helper function to convert time string to minutes
export const timeToMinutes = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};
